import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authFetch } from "../App";

const API = '/api';

// Project Context
const ProjectContext = createContext(null);

export const useProject = () => useContext(ProjectContext);

export function ProjectProvider({ children }) {
  const [activeProject, setActiveProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Load all projects (only if authenticated)
  const loadProjects = useCallback(async () => {
    // Check if we have an auth token before trying to load
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return [];
    }
    
    try {
      const res = await authFetch(`${API}/projects`);
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
        return data;
      }
    } catch (error) {
      console.error("Failed to load projects:", error);
    }
    return [];
  }, []);

  // Initialize on mount and when token changes
  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }
      
      const loadedProjects = await loadProjects();
      
      // Check if we have a saved active project
      const savedProjectId = localStorage.getItem("activeProjectId");
      if (savedProjectId && loadedProjects.length > 0) {
        const project = loadedProjects.find(p => p.id === savedProjectId);
        if (project) {
          setActiveProject(project);
        }
      }
      
      setLoading(false);
      setInitialized(true);
    };
    
    init();
    
    // Listen for storage changes (login/logout)
    const handleStorage = (e) => {
      if (e.key === "token") {
        if (e.newValue) {
          init();
        } else {
          setProjects([]);
          setActiveProject(null);
          setInitialized(false);
        }
      }
    };
    
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [loadProjects]);

  // Update active project from URL params
  useEffect(() => {
    const match = location.pathname.match(/\/app\/projekte\/([^/]+)/);
    if (match && projects.length > 0) {
      const projectId = match[1];
      const project = projects.find(p => p.id === projectId);
      if (project && (!activeProject || activeProject.id !== projectId)) {
        setActiveProject(project);
        localStorage.setItem("activeProjectId", projectId);
      }
    }
  }, [location.pathname, projects, activeProject]);

  // Select a project
  const selectProject = useCallback((project) => {
    setActiveProject(project);
    localStorage.setItem("activeProjectId", project.id);
    navigate(`/app/projekte/${project.id}/dashboard`);
  }, [navigate]);

  // Clear project context
  const clearProject = useCallback(() => {
    setActiveProject(null);
    localStorage.removeItem("activeProjectId");
    navigate("/app");
  }, [navigate]);

  // Refresh projects list
  const refreshProjects = useCallback(async () => {
    const loadedProjects = await loadProjects();
    // Update active project if it still exists
    if (activeProject) {
      const updated = loadedProjects.find(p => p.id === activeProject.id);
      if (updated) {
        setActiveProject(updated);
      }
    }
  }, [loadProjects, activeProject]);

  // Check if we're in a project context
  const isInProjectContext = location.pathname.startsWith("/app/projekte/") && activeProject;

  return (
    <ProjectContext.Provider value={{
      activeProject,
      projects,
      loading,
      selectProject,
      clearProject,
      refreshProjects,
      isInProjectContext,
    }}>
      {children}
    </ProjectContext.Provider>
  );
}
